import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home", id: "nav-home" },
  { to: "/about", label: "About", id: "nav-about" },
  { to: "/services", label: "Services", id: "nav-services" },
  { to: "/careers", label: "Careers", id: "nav-careers" },
  { to: "/blog", label: "Blog", id: "nav-blog" },
  { to: "/contact", label: "Contact", id: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 backdrop-blur-xl ${
        scrolled
          ? "bg-[#050B14]/80 border-b border-white/10"
          : "bg-[#050B14]/40 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 md:h-24 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.id}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? "text-[#00D4FF]"
                    : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00D4FF] text-[#050B14] font-semibold text-sm hover:bg-[#33DDFF] transition-colors"
          >
            Get in Touch
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-white p-2 rounded-md hover:bg-white/5"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden border-t border-white/10 bg-[#050B14]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.id}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm font-medium ${
                    isActive
                      ? "bg-[#00D4FF]/10 text-[#00D4FF]"
                      : "text-slate-200 hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="mt-2 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-[#00D4FF] text-[#050B14] font-semibold text-sm"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
